import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useLocales } from "../../core/i18n";

const STAGGER_MS = 45;

/** One numbered menu row, set large in the display face, in sentence case. */
const MenuRow = ({ item, number, active, onClose }) => (
	<li className="animate-line-reveal" style={{ animationDelay: `${number * STAGGER_MS}ms` }}>
		<Link
			to={item.path}
			onClick={onClose}
			aria-current={active ? "page" : undefined}
			className="group flex items-baseline gap-5 py-3.5 border-b border-pp-rule active:bg-pp-surface"
		>
			<span
				className={`font-mono text-pp-label tracking-pp-label tabular-nums ${active ? "text-pp-accent-text" : "text-pp-muted"}`}
			>
				{String(number).padStart(2, "0")}
			</span>
			<span
				className={`font-sligoil font-bold text-pp-display-lg ${active ? "text-pp-accent" : "text-pp-ink"}`}
			>
				{item.name.charAt(0) + item.name.slice(1).toLowerCase()}
			</span>
		</Link>
	</li>
);

/**
 * The phone menu: a full-screen overlay under the bar listing Home plus the
 * nav `items`, then Contact (unless `showContact` is false) and the language switch. `onClose` runs when a
 * link is followed; `onSwitchLanguage` flips to `nextLang`.
 */
const MobileMenu = ({
	items,
	showContact = true,
	isActive,
	nextLang,
	onSwitchLanguage,
	onClose,
}) => {
	const { t } = useLocales();
	return (
		<div
			id="mobile-menu"
			className="lg:hidden fixed inset-x-0 top-14 sm:top-16 bottom-0 z-40 bg-pp-bg flex flex-col overflow-y-auto overscroll-contain"
			style={{ height: "calc(100dvh - 3.5rem)" }}
		>
			<ul className="flex-1 px-6 sm:px-12 pt-6">
				{[{ name: t("nav.home"), path: "/" }, ...items].map((item, i) => (
					<MenuRow
						key={item.path}
						item={item}
						number={i}
						active={isActive(item.path)}
						onClose={onClose}
					/>
				))}
			</ul>
			<div
				className="px-6 sm:px-12 pt-5 flex items-center gap-3 border-t border-pp-rule animate-line-reveal"
				style={{
					animationDelay: "300ms",
					paddingBottom: "calc(env(safe-area-inset-bottom) + 1.25rem)",
				}}
			>
				{showContact && (
					<Link to="/contact" onClick={onClose} className="pp-btn flex-1 text-center">
						{t("nav.contact")}
					</Link>
				)}
				<button
					type="button"
					onClick={onSwitchLanguage}
					lang={nextLang}
					aria-label={t("ui.switchLanguage")}
					className="pp-btn-ghost min-w-[4.5rem] text-center"
				>
					{nextLang.toUpperCase()}
				</button>
			</div>
		</div>
	);
};

const itemShape = PropTypes.shape({ name: PropTypes.string, path: PropTypes.string });

MenuRow.propTypes = {
	item: itemShape.isRequired,
	number: PropTypes.number.isRequired,
	active: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};
MobileMenu.propTypes = {
	items: PropTypes.arrayOf(itemShape).isRequired,
	showContact: PropTypes.bool,
	isActive: PropTypes.func.isRequired,
	nextLang: PropTypes.string.isRequired,
	onSwitchLanguage: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default MobileMenu;
